import styled from 'styled-components';
import {
  Button,
  Toast,
  ToastType,
} from '@alefeducation/ui-elements-library-core';
import PenSolid from '@alefeducation/ui-elements-library-icons/lib/components/Icons/PenSolid';
import { useState } from 'react';
import ArrowRight from '@alefeducation/ui-elements-library-icons/lib/components/Icons/ArrowRight';
import { useHistory } from 'react-router-dom';
import simpleFileUpload from './simpleFileUpload';
import UploadFiles from '../../components/UploadFiles/UploadFiles';
import downloadFile from '../../utils/fileDownload';
import { PRODUCT_COURSE_MAPPING } from '../../components/Routing/routeHelpers';
import appLocalStorage from '../../utils/AppLocalStorage';

const uploadCards = {
  title: 'Product - Course Mapping',
  fileType: 'csv',
  sizeLimitInBytes: 20_000_000,
  uploadUrl: '/organization-service/api/jobs/product-mapping/csv',
  successMessage: 'Product mapping file is uploaded.',
};

export interface ToastContentType {
  type: ToastType;
  title: string;
  description: string;
}

const BulkUpload = () => {
  const [fileSelected, setFileSelected] = useState<File | null>(null);
  const history = useHistory();
  const [toastData, setToastData] = useState<ToastContentType>({
    type: ToastType.SUCCESS,
    title: '',
    description: '',
  });
  const [showToast, setShowToast] = useState(false);
  const handleFileUploaded = (file: File | null) => {
    setFileSelected(file);
  };

  const getFileLink = `/organization-service/templates/product-course-mapping`;

  const downloadTemplate = async () => {
    try {
      await downloadFile(getFileLink, `product-course-mapping.csv`);
    } catch (exception) {
      console.log('Template download failed');
    }
  };

  const handleUpload = (file: File) => {
    simpleFileUpload(uploadCards.uploadUrl, file).then(
      () => {
        setToastData({
          type: ToastType.SUCCESS,
          title: 'Success! Your file has been imported.',
          description: uploadCards.successMessage,
        });
        setShowToast(true);
        history.push(PRODUCT_COURSE_MAPPING.productCourseMapping);
      },
      (e: any) => {
        setToastData({
          type: ToastType.ERROR,
          title: 'Upload Failed',
          description: e?.message,
        });
        setShowToast(true);
      },
    );
  };

  const onToastHide = (): void => {
    setShowToast(false);
    setToastData({
      type: ToastType.SUCCESS,
      title: '',
      description: '',
    });
  };

  const uploadFile = () => {
    if (fileSelected) {
      handleUpload(fileSelected);
    }
  };

  return (
    <BulkUploadContainer>
      <StyledTable>
        <BulkUploadWizard>
          <BulkUploadMain>
            <BulkUploadTitle>Bulk Upload & Mapping</BulkUploadTitle>
            <BulkUploadNavigator>
              <StepperItem>
                <CurrentStepper>
                  <PenSolid
                    color="info.160"
                    data-testid="current-stepper-item"
                  />
                </CurrentStepper>
                <p className="stepper__item__label">Upload File</p>
              </StepperItem>
            </BulkUploadNavigator>
            <div>
              <UploadFiles
                onFilesUploaded={handleFileUploaded}
                uploadedFile={fileSelected}
              />
              {!fileSelected && (
                <DownloadTemplate onClick={downloadTemplate}>
                  Download our Template
                </DownloadTemplate>
              )}
            </div>
          </BulkUploadMain>
          <BulkUploadActions>
            <Button
              variant="secondary"
              onClick={() =>
                history.push(PRODUCT_COURSE_MAPPING.productCourseMapping)
              }
            >
              Cancel
            </Button>

            {!appLocalStorage.isViewOnlyRole() && (
              <Button
                variant="primary"
                icon={<ArrowRight />}
                iconPosition="right"
                disabled={fileSelected?.type !== 'text/csv'}
                onClick={uploadFile}
              >
                Upload
              </Button>
            )}
          </BulkUploadActions>
        </BulkUploadWizard>
      </StyledTable>
      {showToast && (
        <Toast
          key={Math.random()}
          autoHide
          content={{
            title: toastData.title,
            description: toastData.description,
          }}
          onHide={onToastHide}
          data-testid={`toast-${toastData.type}`}
          type={toastData.type}
        />
      )}
    </BulkUploadContainer>
  );
};

export default BulkUpload;

const BulkUploadContainer = styled.div`
  height: 100%;
`;

const BulkUploadMain = styled.div`
  padding: 0 40px;
`;

const BulkUploadTitle = styled.div`
  font-size: 24px;
  font-weight: 700;
  line-height: 32px;
`;

const BulkUploadWizard = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 75vh;
`;

const BulkUploadActions = styled.div`
  padding: 24px 24px;
  display: flex;
  justify-content: space-between;
  border-top: 1px solid ${({ theme }: any) => theme.colors.primary[20]};
`;

const BulkUploadNavigator = styled.div`
  padding: 24px 0 24px;
  border-bottom: 1px solid ${({ theme }: any) => theme.colors.primary[20]};
  margin-bottom: 24px;
`;

const DownloadTemplate = styled.div`
  text-decoration: underline;
  background-color: ${({ theme }: any) => theme.colors.primary[0]};
  border: none;
  cursor: pointer;
  font-size: 16px;
  margin-top: 10px;
  color: ${(props) => props.theme.colors.primary[100]};
  font-weight: 300;
`;

const StepperItem = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const CurrentStepper = styled.span`
  font-size: 14px;
  font-weight: 700;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 32px;
  width: 32px;
  background-color: ${({ theme }: any) => theme.colors.info[30]};
  border-radius: 50%;
`;

export const StyledTable = styled.div`
  background: white;
  border-radius: 10px;
  height: 100%;
  padding: 24px;

  .loading,
  .empty-message-view {
    height: 80%;
  }
`;
